import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with default config
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Handle unauthorized access
            localStorage.removeItem('authToken');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// API endpoints
export const api = {
    // Auth endpoints
    auth: {
        me: () => apiClient.get('/api/auth/me'),
        login: () => apiClient.get('/api/auth/login'),
        logout: () => apiClient.post('/api/auth/logout'),
    },

    // Items endpoints
    items: {
        getAll: () => apiClient.get('/api/items'),
        getById: (id) => apiClient.get(`/api/items/${id}`),
        create: (data) => apiClient.post('/api/items', data),
        update: (id, data) => apiClient.put(`/api/items/${id}`, data),
        delete: (id) => apiClient.delete(`/api/items/${id}`),
    },

    // Files endpoints
    files: {
        upload: (formData) => apiClient.post('/api/files/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        getAll: () => apiClient.get('/api/files'),
    },

    // Resource tag management endpoints
    resourceTags: {
        attach: (resourceId, tagId) => apiClient.post(`/api/resources/${resourceId}/tags/${tagId}`),
        detach: (resourceId, tagId) => apiClient.delete(`/api/resources/${resourceId}/tags/${tagId}`),
        updateAll: (resourceId, tagIds) => apiClient.put(`/api/resources/${resourceId}/tags`, { tag_ids: tagIds }),
    },

    // Borrowing endpoints
    borrowings: {
        getAll: (params) => apiClient.get('/api/borrowings', { params }),
        getMine: (params) => apiClient.get('/api/borrowings/mine', { params }),
        getById: (id) => apiClient.get(`/api/borrowings/${id}`),
        create: (data) => apiClient.post('/api/borrowings', data),
        update: (id, data) => apiClient.put(`/api/borrowings/${id}`, data),
        borrow: (id, data) => apiClient.patch(`/api/borrowings/${id}/borrow`, data),
        return: (id, data) => apiClient.patch(`/api/borrowings/${id}/return`, data),
        delete: (id) => apiClient.delete(`/api/borrowings/${id}`),
        getUserBorrowings: (userId) => apiClient.get(`/api/borrowings/user/${userId}`),
        getOverdue: () => apiClient.get('/api/borrowings/overdue'),
        checkResourceAvailability: (resourceId) => apiClient.get(`/api/borrowings/resource/${resourceId}/availability`),

        // Reservation-specific endpoints (now part of borrowings)
        getReservations: (params) => apiClient.get('/api/borrowings/reservations', { params }),
        getMyReservations: () => apiClient.get('/api/borrowings/user/me/reservations'),
        convertToActualBorrowing: (id) => apiClient.patch(`/api/borrowings/${id}/convert-to-borrowing`),
        checkReservationConflicts: (resourceId, params) => apiClient.get(`/api/borrowings/resource/${resourceId}/reservation-conflicts`, { params }),

        // Reservation validation endpoints
        getPendingValidations: (params) => apiClient.get('/api/borrowings/pending-validation', { params }),
        validate: (id, data) => apiClient.patch(`/api/borrowings/${id}/validate`, data),
    },

    // Consumption endpoints
    consumptions: {
        getAll: (params) => apiClient.get('/api/consumptions', { params }),
        getById: (id) => apiClient.get(`/api/consumptions/${id}`),
        create: (data) => apiClient.post('/api/consumptions', data),
        getUserHistory: (userId) => apiClient.get(`/api/consumptions/user/${userId}`),
        getStatistics: () => apiClient.get('/api/consumptions/statistics'),
        getResourceStats: (resourceId) => apiClient.get(`/api/consumptions/resource/${resourceId}/stats`),
    },

    // Refill endpoints
    refills: {
        getAll: (params) => apiClient.get('/api/refills', { params }),
        getById: (id) => apiClient.get(`/api/refills/${id}`),
        create: (data) => apiClient.post('/api/refills', data),
        update: (id, data) => apiClient.put(`/api/refills/${id}`, data),
        batchCreate: (data) => apiClient.post('/api/refills/batch', data),
        getUserHistory: (userId) => apiClient.get(`/api/refills/user/${userId}`),
        getRecent: () => apiClient.get('/api/refills/recent'),
    },

    // Reservation managers endpoints
    reservationManagers: {
        getForResource: (resourceId) => apiClient.get(`/api/resources/${resourceId}/reservation-managers`),
        add: (resourceId, data) => apiClient.post(`/api/resources/${resourceId}/reservation-managers`, data),
        remove: (resourceId, userId) => apiClient.delete(`/api/resources/${resourceId}/reservation-managers/${userId}`),
        setAll: (resourceId, userIds) => apiClient.put(`/api/resources/${resourceId}/reservation-managers`, { user_ids: userIds }),
    },

    // Enhanced resource endpoints
    resources: {
        getAll: (params) => apiClient.get('/api/resources', { params }),
        getById: (id) => apiClient.get(`/api/resources/${id}`),
        create: (data) => apiClient.post('/api/resources', data),
        update: (id, data) => apiClient.put(`/api/resources/${id}`, data),
        delete: (id) => apiClient.delete(`/api/resources/${id}`),
        getAvailability: (id) => apiClient.get(`/api/resources/${id}/availability`),
        getTransactions: (id) => apiClient.get(`/api/resources/${id}/transactions`),
        getUsageStats: (id) => apiClient.get(`/api/resources/${id}/usage-stats`),
        getLowStock: () => apiClient.get('/api/resources/low-stock'),
        checkAvailabilityBatch: (resourceIds) => apiClient.get('/api/resources/availability/batch', {
            params: { resource_ids: resourceIds.join(',') }
        }),
        getQuantitiesBatch: (resourceIds) => apiClient.post('/api/resources/quantities/batch', { resource_ids: resourceIds }),

        // Resource-specific transaction endpoints
        getBorrowings: (id, params) => apiClient.get(`/api/resources/${id}/borrowings`, { params }),
        getConsumptions: (id, params) => apiClient.get(`/api/resources/${id}/consumptions`, { params }),
        getRefills: (id, params) => apiClient.get(`/api/resources/${id}/refills`, { params }),
    },

    // Metadata endpoints
    metadata: {
        // Metadata definitions
        getDefinitions: (params) => apiClient.get('/api/metadata/definitions', { params }),
        getDefinitionById: (id) => apiClient.get(`/api/metadata/definitions/${id}`),
        getDefinitionByKey: (key) => apiClient.get(`/api/metadata/definitions/key/${key}`),
        createDefinition: (data) => apiClient.post('/api/metadata/definitions', data),
        updateDefinition: (id, data) => apiClient.put(`/api/metadata/definitions/${id}`, data),
        deleteDefinition: (id) => apiClient.delete(`/api/metadata/definitions/${id}`),

        // Resource metadata
        getResourceMetadata: (resourceId) => apiClient.get(`/api/metadata/resources/${resourceId}`),
        getResourceMetadataByKey: (resourceId, key) => apiClient.get(`/api/metadata/resources/${resourceId}/${key}`),
        createResourceMetadata: (data) => apiClient.post('/api/metadata/resources', data),
        updateResourceMetadata: (resourceId, key, data) => apiClient.put(`/api/metadata/resources/${resourceId}/${key}`, data),
        upsertResourceMetadata: (resourceId, key, value) => apiClient.put(`/api/metadata/resources/${resourceId}/${key}/upsert`, JSON.stringify(value)),
        deleteResourceMetadata: (resourceId, key) => apiClient.delete(`/api/metadata/resources/${resourceId}/${key}`),
    },

    // Users endpoints
    users: {
        getAll: (params) => apiClient.get('/api/users', { params }),
        getById: (id) => apiClient.get(`/api/users/${id}`),
        create: (data) => apiClient.post('/api/users', data),
        update: (id, data) => apiClient.put(`/api/users/${id}`, data),
        delete: (id) => apiClient.delete(`/api/users/${id}`),
        getActive: () => apiClient.get('/api/users/active/all'),
        getBorrowings: (id, params) => apiClient.get(`/api/borrowings/user/${id}`, { params }),
        getSettings: (id) => apiClient.get(`/api/users/${id}/settings`),
        updateSettings: (id, settings) => apiClient.put(`/api/users/${id}/settings`, { settings }),
    },

    // Locations endpoints
    locations: {
        getAll: (params) => apiClient.get('/api/locations', { params }),
        getById: (id) => apiClient.get(`/api/locations/${id}`),
        create: (data) => apiClient.post('/api/locations', data),
        update: (id, data) => apiClient.put(`/api/locations/${id}`, data),
        delete: (id) => apiClient.delete(`/api/locations/${id}`),
        getBorrowings: (id, params) => apiClient.get(`/api/locations/${id}/borrowings`, { params }),
        getConsumptions: (id, params) => apiClient.get(`/api/locations/${id}/consumptions`, { params }),
        getRefills: (id, params) => apiClient.get(`/api/locations/${id}/refills`, { params }),
    },

    // Categories endpoints
    categories: {
        getAll: (params) => apiClient.get('/api/categories', { params }),
        getById: (id) => apiClient.get(`/api/categories/${id}`),
        create: (data) => apiClient.post('/api/categories', data),
        update: (id, data) => apiClient.put(`/api/categories/${id}`, data),
        delete: (id) => apiClient.delete(`/api/categories/${id}`),
        getBorrowingFieldDefinitions: (id) => apiClient.get(`/api/categories/${id}/borrowing-field-definitions`),
        updateBorrowingFieldDefinitions: (id, definitions) => apiClient.put(`/api/categories/${id}/borrowing-field-definitions`, definitions),
        getCustomAttributeDefinitions: (id) => apiClient.get(`/api/categories/${id}/custom-attribute-definitions`),
        updateCustomAttributeDefinitions: (id, definitions) => apiClient.put(`/api/categories/${id}/custom-attribute-definitions`, definitions),
    },

    // Authorization endpoints
    authorization: {
        canPerform: (params) => apiClient.get('/api/action-auth/can-perform', { params }),
        capabilities: () => apiClient.get('/api/action-auth/capabilities'),
        resourcePermissions: (resourceId) => apiClient.get(`/api/action-auth/resource/${resourceId}/permissions`),
        locationPermissions: (locationId) => apiClient.get(`/api/action-auth/location/${locationId}/permissions`),
        borrowingPermissions: (borrowingId) => apiClient.get(`/api/action-auth/borrowing/${borrowingId}/permissions`),
        bulkPermissions: (data) => apiClient.post('/api/action-auth/bulk-permissions', data),
    },

    // Permissions endpoints
    permissions: {
        getAll: (params) => apiClient.get('/api/permissions', { params }),
        getById: (id) => apiClient.get(`/api/permissions/${id}`),
        getUserPermissions: (userId) => apiClient.get(`/api/permissions/user/${userId}`),
        create: (data) => apiClient.post('/api/permissions', data),
        update: (id, data) => apiClient.put(`/api/permissions/${id}`, data),
        delete: (id) => apiClient.delete(`/api/permissions/${id}`),
        grant: (params) => apiClient.post('/api/permissions/grant', null, { params }),
        revoke: (params) => apiClient.delete('/api/permissions/revoke', { params }),
        check: (permissionType, params) => apiClient.get(`/api/permissions/check/${permissionType}`, { params }),
    },

    // Attachments endpoints
    attachments: {
        uploadAndAttach: (formData) => apiClient.post('/api/attachments/upload-and-attach', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        getEntityAttachments: (entityType, entityId, context) => {
            const url = `/api/attachments/${entityType}/${entityId}${context ? `?context=${context}` : ''}`;
            return apiClient.get(url);
        },
        uploadToResource: (resourceId, formData) => apiClient.post(`/api/attachments/resources/${resourceId}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        uploadToBorrowing: (borrowingId, formData) => apiClient.post(`/api/attachments/borrowings/${borrowingId}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        uploadToLocation: (locationId, formData) => apiClient.post(`/api/attachments/locations/${locationId}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        uploadToCategory: (categoryId, formData) => apiClient.post(`/api/attachments/categories/${categoryId}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        cropImage: (attachmentId, formData) => apiClient.post(`/api/attachments/${attachmentId}/crop`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
        delete: (attachmentId, deleteFile = true) => apiClient.delete(`/api/attachments/${attachmentId}?delete_file=${deleteFile}`),
        updateOrder: (attachmentId, newOrder) => apiClient.patch(`/api/attachments/${attachmentId}/order`, new URLSearchParams({ new_order: newOrder }), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }),
        reorder: (attachmentId, direction) => apiClient.patch(`/api/attachments/${attachmentId}/reorder`, new URLSearchParams({ direction }), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        }),
        fixOrders: (entityType, entityId, context = null) => {
            const url = `/api/attachments/${entityType}/${entityId}/fix-orders${context ? `?context=${context}` : ''}`;
            return apiClient.patch(url);
        },
    },

    // AI Image Generation endpoints
    imagen: {
        editImage: (data) => apiClient.post('/api/imagen/edit', data),
        generateImage: (data) => apiClient.post('/api/imagen/generate', data),
        generateForEntity: (data) => apiClient.post('/api/imagen/generate-for-entity', data),
        getModels: () => apiClient.get('/api/imagen/models'),
        getConfig: () => apiClient.get('/api/imagen/config'),
        healthCheck: () => apiClient.get('/api/imagen/health'),
    },

    // Push notification endpoints
    push: {
        subscribe: (data) => apiClient.post('/api/push/subscribe', data),
        unsubscribe: (data) => apiClient.delete('/api/push/unsubscribe', { data }),
        getSubscriptions: () => apiClient.get('/api/push/subscriptions'),
        getConfig: () => apiClient.get('/api/push/config'),
        send: (data) => apiClient.post('/api/push/send', data),
        test: (subscriptionId) => apiClient.post(`/api/push/test/${subscriptionId}`),
    },

    // Direct API calls for admin functions
    get: (url) => apiClient.get(`/api${url}`),
    post: (url, data) => apiClient.post(`/api${url}`, data),
    put: (url, data) => apiClient.put(`/api${url}`, data),
    patch: (url, data, config) => apiClient.patch(`/api${url}`, data, config),
    delete: (url) => apiClient.delete(`/api${url}`),
};

export default api;